/* --- File: src/context/UserContext.js (BẢN CẬP NHẬT ĐẦY ĐỦ CHO LỚP HỌC TRỰC TUYẾN) --- */
import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { auth, db } from '../firebaseConfig';
import { onAuthStateChanged } from 'firebase/auth';
import { collection, onSnapshot, query, where, documentId } from 'firebase/firestore';
import { subscribeToClasses } from '../services/firebase/classService';
import { fetchFirestoreDoc } from '../hooks/useFirestore';
import { hasRole as hasRoleUtil, getRoles as getRolesUtil } from '../utils/roles';

import { subscribeIn } from '../services/firebase/scopedSubscription';

const UserContext = createContext();

export const useAuth = () => useContext(UserContext);

export const UserProvider = ({ children }) => {
    const [userData, setUserData] = useState(null);
    const [allUsers, setAllUsers] = useState([]);
    const [allClasses, setAllClasses] = useState([]);
    const [loading, setLoading] = useState(true);

    // 1. Lắng nghe Firebase Auth & Tải dữ liệu tự động
    useEffect(() => {
        let generation = 0;
        let stopClasses = () => {};
        let stopUsers = () => {};
        const stopSession = () => {
            stopClasses();
            stopUsers();
            stopClasses = () => {};
            stopUsers = () => {};
        };
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            const session = ++generation;
            stopSession();
            setLoading(true);
            setUserData(null);
            setAllClasses([]);
            setAllUsers([]);
            try {
                if (!user) return;
                const userDoc = await fetchFirestoreDoc('users', user.uid);
                if (session !== generation) return;
                if (!userDoc) return;
                setUserData({ auth: user, firestore: userDoc });
                const isLearner = hasRoleUtil(userDoc, ['student', 'parent']) && !hasRoleUtil(userDoc, ['admin', 'teacher', 'coordinator']);
                if (isLearner) {
                    const studentId = hasRoleUtil(userDoc, 'student') ? user.uid : userDoc.studentId;
                    setAllUsers([{ ...userDoc, key: user.uid, uid: user.uid }]);
                    if (studentId) stopClasses = onSnapshot(query(collection(db, 'classes'), where('studentIds', 'array-contains', studentId)), snapshot => {
                        if (session !== generation) return;
                        const classes = snapshot.docs.map(d => ({ ...d.data(), key: d.id }));
                        setAllClasses(classes);
                        stopUsers();
                        const ids = [user.uid, studentId, ...classes.flatMap(c => [c.teacherId, ...(c.teacherIds || [])])];
                        stopUsers = subscribeIn('users', documentId(), ids, users => {
                            if (session === generation) setAllUsers(users);
                        });
                    }, error => console.error('Lỗi tải lớp học:', error));
                } else {
                    stopClasses = subscribeToClasses(classes => {
                        if (session === generation) setAllClasses(classes);
                    });
                    stopUsers = onSnapshot(collection(db, 'users'), snapshot => {
                        if (session === generation) setAllUsers(snapshot.docs.map(d => ({ ...d.data(), key: d.id, uid: d.id })));
                    }, error => console.error('Lỗi tải danh sách người dùng:', error));
                }
            } catch (error) {
                if (session === generation) console.error('Lỗi lấy dữ liệu từ Firestore:', error);
            } finally {
                if (session === generation) setLoading(false);
            }
        });
        return () => {
            ++generation;
            unsubscribe();
            stopSession();
        };
    }, []);

    // 2. Hàm Đăng nhập thủ công cho FaceID (using hook)
    const loginWithFaceId = useCallback(async (uid) => {
        setLoading(true);
        try {
            const userDoc = await fetchFirestoreDoc('users', uid);

            if (userDoc) {
                const fakeAuthUser = {
                    uid: uid,
                    email: userDoc.email,
                    displayName: userDoc.displayName,
                    photoURL: userDoc.photoURL,
                    emailVerified: true
                };

                setUserData({
                    auth: fakeAuthUser,
                    firestore: userDoc
                });
                return true;
            }
        } catch (error) {
            console.error("Lỗi login FaceID:", error);
        } finally {
            setLoading(false);
        }
        return false;
    }, []);

    // 3. Hàm Đăng xuất
    const logout = useCallback(async () => {
        setLoading(true); 
        try {
            await auth.signOut();
            setUserData(null);
            setAllClasses([]);
            setAllUsers([]);
        } catch (error) {
            console.error("Lỗi đăng xuất:", error);
        }
        setLoading(false);
    }, []);

    // Kiểm tra người dùng ĐANG ĐĂNG NHẬP có 1 (hoặc nhiều) vai trò cụ thể không —
    // hỗ trợ 1 người có nhiều vai trò cùng lúc. Dùng: hasRole('teacher') hoặc
    // hasRole(['admin','teacher']). Bọc useCallback để giữ tham chiếu ổn định
    // qua các lần render (tránh trigger lại useEffect/useMemo phụ thuộc vào nó).
    const hasRole = useCallback((checkRoles) => hasRoleUtil(userData?.firestore, checkRoles), [userData]);
    const currentRoles = useMemo(() => getRolesUtil(userData?.firestore), [userData]);
    const value = useMemo(() => ({ userData, allUsers, allClasses, loading, loginWithFaceId, logout, hasRole, currentRoles }),
        [userData, allUsers, allClasses, loading, loginWithFaceId, logout, hasRole, currentRoles]);

    return (
        <UserContext.Provider value={value}>
            {children}
        </UserContext.Provider>
    );
};